// Botón flotante para abrir Eruda mobile devtools on-demand. Reemplazo
// del auto-load anterior (que se filtraba a previews Vercel via hostname
// guard insuficiente). Visible siempre durante development phase — Juan
// y QA piloto saben que el producto está en desarrollo. Cuando llegue
// release productiva se esconde detrás de env var DEV-only o se remueve.
//
// Eruda se inyecta lazy: script tag al head el primer click, eruda.show()
// los clicks siguientes. Si la red bloquea el CDN, el handler logguea
// pero no rompe nada — el botón sigue clickeable.

import { useState } from "react";
import { Bug } from "lucide-react";

// Tipado mínimo de la global que Eruda inyecta. Solo los métodos que
// usamos — no importamos los types del paquete (no está en deps).
declare global {
  interface Window {
    eruda?: {
      init: () => void;
      show: () => void;
    };
  }
}

const ERUDA_CDN = "https://cdn.jsdelivr.net/npm/eruda";

/** Carga Eruda dinámicamente la primera vez. Idempotente: clicks
 *  sucesivos solo llaman a show(). Promise para que el caller pueda
 *  esperar el load y mostrar el panel sin race. */
function loadEruda(): Promise<void> {
  if (window.eruda) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${ERUDA_CDN}"]`,
    ) as HTMLScriptElement | null;
    if (existing) {
      // Otro click disparó el load — esperamos a window.eruda
      existing.addEventListener("load", () => {
        window.eruda?.init();
        resolve();
      });
      existing.addEventListener("error", () =>
        reject(new Error("Eruda script failed to load (existing tag)")),
      );
      return;
    }
    const script = document.createElement("script");
    script.src = ERUDA_CDN;
    script.async = true;
    script.onload = () => {
      window.eruda?.init();
      resolve();
    };
    script.onerror = () =>
      reject(new Error("Eruda script failed to load from CDN"));
    document.head.appendChild(script);
  });
}

export function DebugToolsToggle() {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await loadEruda();
      window.eruda?.show();
    } catch (e) {
      console.warn("[DebugToolsToggle] no se pudo cargar Eruda:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      title="Debug tools"
      aria-label="Abrir herramientas de debugging"
      className="fixed md:hidden bottom-20 right-4 z-[60] h-10 w-10 rounded-full bg-card/80 backdrop-blur-sm border border-border shadow-sm text-muted-foreground hover:text-foreground hover:border-muted-foreground/50 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Bug className="h-[18px] w-[18px]" strokeWidth={2} />
    </button>
  );
}
